import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

function getLangFromPath() {
  const path = window.location.pathname;
  if (path.startsWith("/pt")) return "pt";
  if (path.startsWith("/de")) return "de";
  return "en";
}

function getBasePath() {
  const path = window.location.pathname;
  if (path.startsWith("/pt")) return "/pt";
  if (path.startsWith("/de")) return "/de";
  return "";
}

export default function Home() {
  const { t } = useTranslation();
  const lang = getLangFromPath();
  const basePath = getBasePath();

  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang]);

  const articleList = useMemo(() => {
    return articles[lang as keyof typeof articles] || articles.en;
  }, [lang]);

  const heroImage = "https://readdy.ai/api/search-image?query=Overhead%20flat%20lay%20of%20colorful%20sneakers%20hoodies%20caps%20and%20bags%20neatly%20arranged%20on%20a%20soft%20sage%20green%20background%20with%20a%20laptop%20showing%20a%20spreadsheet%20bright%20editorial%20photography%20vivid%20accent%20colors%20clean%20modern%20aesthetic&width=1600&height=900&seq=hero-001&orientation=landscape&nocache=true";

  const howStepImages = [
    "https://readdy.ai/api/search-image?query=Hand%20pointing%20at%20a%20grid%20of%20product%20category%20cards%20on%20a%20tablet%20screen%20bright%20clean%20editorial%20photography%20soft%20natural%20light%20organized%20choosing%20a%20category%20theme%20warm%20neutral%20tones&width=600&height=400&seq=how-step-001&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Two%20product%20cards%20side%20by%20side%20being%20compared%20with%20a%20checklist%20and%20magnifying%20glass%20on%20a%20clean%20desk%20bright%20editorial%20photography%20comparison%20theme%20soft%20daylight%20organized%20neutral%20tones&width=600&height=400&seq=how-step-002&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Person%20checking%20a%20checklist%20on%20a%20laptop%20with%20a%20green%20checkmark%20and%20product%20photos%20on%20screen%20verification%20and%20confirmation%20theme%20bright%20clean%20editorial%20photography%20soft%20lighting%20warm%20tones&width=600&height=400&seq=how-step-003&orientation=landscape"
  ];

  return (
    <div className="min-h-screen bg-background-50 font-body">
      <Navbar />

      <section className="relative h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <img
          src={heroImage}
          alt="CSSBuy Spreadsheet fashion products flat lay"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30" />
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto w-full">
          <p className="text-xs md:text-sm font-medium tracking-widest uppercase text-background-50/80 mb-3">
            {t("hero.eyebrow")}
          </p>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-background-50 mb-4 leading-tight">
            {t("hero.title")}
          </h1>
          <p className="text-sm md:text-base lg:text-lg text-background-50/90 mb-6 max-w-2xl mx-auto">
            {t("hero.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="bg-primary-500 text-background-50 px-5 py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t("hero.cta_primary")}
            </a>
            <a
              href="#categories"
              className="border border-background-50/40 text-background-50 px-5 py-2.5 rounded-md text-sm font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t("hero.cta_secondary")}
            </a>
          </div>
          <p className="text-xs text-background-50/70 mt-4">
            {t("hero.trust")}
          </p>
        </div>
      </section>

      <section className="py-10 md:py-14 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-10 max-w-5xl mx-auto text-center">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="bg-primary-500 text-background-50 px-6 py-3 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t("cta.main_site")}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="bg-accent-500 text-background-50 px-6 py-3 rounded-md text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t("cta.buy_now")}
            </a>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-10 max-w-4xl mx-auto text-center">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground-950 mb-4">
            {t("what.title")}
          </h2>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
            {t("what.description")}
          </p>
          <p className="text-xs md:text-sm text-foreground-500 italic mb-6">
            {t("what.warning")}
          </p>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-block bg-primary-500 text-background-50 px-5 py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t("what.cta")}
          </a>
        </div>
      </section>

      <section id="categories" className="py-12 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground-950 mb-3">
              {t("categories.title")}
            </h2>
            <p className="text-sm md:text-base text-foreground-600">
              {t("categories.subtitle")}
            </p>
          </div>
          <div className="flex justify-center mb-6">
            <a
              href={`${basePath}/products`}
              className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-store-2-line"></i>
              {t("products.page_title") || "Browse Product Directory"}
            </a>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4 max-w-6xl mx-auto">
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group relative rounded-lg overflow-hidden aspect-[4/3] bg-background-200 block cursor-pointer"
              >
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="text-sm font-medium text-background-50">
                    {cat.name}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-10 max-w-5xl mx-auto">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground-950 text-center mb-10 md:mb-14">
            {t("how.title")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                className="bg-background-50 rounded-lg overflow-hidden border border-background-200/40"
              >
                <div className="aspect-[16/10] overflow-hidden bg-background-200">
                  <img
                    src={howStepImages[step - 1]}
                    alt={t(`how.step${step}_title`)}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-5 md:p-6">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-sm mb-4">
                    0{step}
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">
                    {t(`how.step${step}_title`)}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {t(`how.step${step}_desc`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-block bg-primary-500 text-background-50 px-5 py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t("how.cta")}
            </a>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-10 max-w-4xl mx-auto text-center">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground-950 mb-4">
            {t("qc.title")}
          </h2>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
            {t("qc.description")}
          </p>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-block bg-accent-500 text-background-50 px-5 py-2.5 rounded-md text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t("qc.cta")}
          </a>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-10 max-w-4xl mx-auto text-center">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground-950 mb-4">
            {t("shipping.title")}
          </h2>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
            {t("shipping.description")}
          </p>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-block bg-secondary-500 text-background-50 px-5 py-2.5 rounded-md text-sm font-medium hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t("shipping.cta")}
          </a>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-10 max-w-5xl mx-auto">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground-950 text-center mb-8 md:mb-12">
            {t("safety.title")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
              <div
                key={item}
                className="flex items-start gap-2.5 bg-background-100 rounded-lg p-3 md:p-4 border border-background-200/40"
              >
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                  <i className="ri-check-line text-accent-600 text-sm"></i>
                </div>
                <p className="text-sm text-foreground-700">
                  {t(`safety.item${item}`)}
                </p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-block bg-accent-500 text-background-50 px-5 py-2.5 rounded-md text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t("safety.cta")}
            </a>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-10 max-w-5xl mx-auto">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground-950 text-center mb-8 md:mb-12">
            {t("articles.title")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {articleList.map((a) => (
              <a
                key={a.slug}
                href={`${basePath}/article/${a.slug}`}
                className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/40 hover:border-primary-300/60 transition-colors cursor-pointer flex flex-col"
              >
                <div className="aspect-[16/9] overflow-hidden bg-background-200">
                  <img
                    src={a.heroImage}
                    alt={a.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="p-4 md:p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium text-accent-700 bg-accent-100/60 px-2 py-0.5 rounded">
                      {a.category}
                    </span>
                    <span className="text-xs text-foreground-500">
                      {a.readTime} {t("articles.read_time")}
                    </span>
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-700 transition-colors">
                    {a.title}
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-600 line-clamp-2 mb-3">
                    {a.metaDescription}
                  </p>
                  <span className="mt-auto text-sm font-medium text-primary-600 group-hover:text-primary-700 transition-colors">
                    {t("articles.read_more")} &rarr;
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-primary-500">
        <div className="w-full px-4 md:px-6 lg:px-10 max-w-4xl mx-auto text-center">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-background-50 mb-4">
            {t("final.title")}
          </h2>
          <p className="text-sm md:text-base text-background-50/90 mb-6 max-w-2xl mx-auto">
            {t("final.description")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="bg-background-50 text-primary-700 px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t("final.cta_primary")}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="border border-background-50/40 text-background-50 px-5 py-2.5 rounded-md text-sm font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t("final.cta_secondary")}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}